'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useSyncExternalStore } from 'react';
import {
  bootstrap,
  getServerSnapshot,
  getSnapshot,
  subscribe,
} from '@/lib/auth';
import { AppTermsGate } from '@/components/AppTermsGate';

/**
 * Wrap a page's data UI in this. If the user isn't authenticated, redirect
 * them to /login; while we're spending the refresh token to revive a stored
 * session, render nothing (no flash of "log in" before we know).
 *
 * This is also the only component that calls `bootstrap()` — the very first
 * client-side mount of any page kicks off the refresh-token spend, which
 * populates the access token + /me profile in module memory.
 */

let bootstrapped = false;

export function NeedsAuth({ children }: { children: React.ReactNode }) {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const router = useRouter();

  useEffect(() => {
    if (bootstrapped) return;
    bootstrapped = true;
    void bootstrap();
  }, []);

  useEffect(() => {
    if (state.status === 'unauthenticated') {
      router.replace('/login');
    }
  }, [state.status, router]);

  if (state.status === 'loading' || state.status === 'unauthenticated') {
    // The redirect above will fire on `unauthenticated`; render nothing in
    // the meantime so the gated page never flashes.
    return null;
  }
  // Authenticated, but the app-wide Terms of Use haven't been accepted yet.
  // The server 412s every data route until they are, so block the page with
  // the acceptance gate. On accept it re-pulls /me and this flips back.
  if (state.me?.app_terms_required) {
    return <AppTermsGate />;
  }
  return <>{children}</>;
}
